import { createAppSlice } from '.';
import {
  deleteDriverBeaconEntry as deleteDriverBeaconEntryApi,
  getBeacon,
  getBeaconDriverHistory,
  getBeaconEquipmentHistory,
  getBeacons,
  linkBeaconToDriver as linkBeaconToDriverApi,
  linkBeaconToEquipment as linkBeaconToEquipmentApi,
  linkTagToBeacon as linkTagToBeaconApi,
  unlinkDriverBeacon as unlinkDriverBeaconApi,
  unlinkTagFromBeacon as unlinkTagFromBeaconApi,
  updateDriverBeaconEntry as updateDriverBeaconEntryApi,
} from '../api/api';
import { beacon as beaconSchema } from '../api/schema';
import { BeaconType, DeviceStateWithCluster, DeviceStatus } from '../constants/deviceConstants';
import { normalize } from '../utils';
import { ItemWithPermissions } from '../utils/permissions';
import { KeysToCamelCase } from '../utils/types';
import { ClusterId } from './clusters';
import { EquipmentId } from './equipments';
import { alerting, success } from './snacks';
import { Tag, TagId } from './tags';
import { UserId, driverAndBeaconLinked } from './users';

type ActiveSession = {
  from_date: string;
  device_session_id: string;
  parent_device_session_id: string;
  device_instance_id: number;
  track?: string;
};

export type BeaconUUID = string;
export type Beacon = ItemWithPermissions & {
  uuid: BeaconUUID;
  status: DeviceStatus;
  cluster_id: ClusterId;
  current_equipment_instance_id?: EquipmentId;
  associate_date: string;
  location_longitude?: number;
  location_latitude?: number;
  location_date?: string;
  lastinfo_date?: string;
  type: BeaconType;
  current_state_with_cluster: DeviceStateWithCluster;
  soc?: number;
  readable_uuid: string;
  active_session?: ActiveSession;
  tags?: Tag[];
  current_user_id?: UserId;
};
export type NormalizedBeacon = KeysToCamelCase<Beacon>;

type BeaconEquipmentHistoryId = number;
export type BeaconEquipmentHistory = {
  id: BeaconEquipmentHistoryId;
  beacon_uuid: BeaconUUID;
  equipment_instance_id: EquipmentId;
  from_date: string;
  to_date: string | null;
};

type BeaconDriverHistoryId = string;
export type BeaconDriverHistory = {
  id: BeaconDriverHistoryId;
  beacon_uuid: BeaconUUID;
  user_id: UserId;
  from_date: string;
  to_date?: string;
};

type State = {
  beaconsIDs: BeaconUUID[];
  beaconsById: Record<BeaconUUID, NormalizedBeacon>;
  beaconEquipmentHistoryByID: Record<BeaconUUID, BeaconEquipmentHistory[]>;
  beaconDriverHistoryByID: Record<BeaconUUID, BeaconDriverHistory[]>;
  isDataFetch: boolean;
};

const initialState: State = {
  beaconsIDs: [],
  beaconsById: {},
  beaconEquipmentHistoryByID: {},
  beaconDriverHistoryByID: {},
  isDataFetch: false,
};

const beaconsSlice = createAppSlice({
  name: 'beacons',
  initialState,
  reducers: (create) => ({
    fetchBeacons: create.asyncThunk(
      async (_, { dispatch }) => {
        const beacons = (await alerting(() => getBeacons(), { dispatch })) as Beacon[];
        return beacons;
      },
      {
        fulfilled: (state, { payload }) => {
          const normalized = normalize(payload, [beaconSchema]) as {
            entities: { beacon: Record<BeaconUUID, NormalizedBeacon> };
            result: BeaconUUID[];
          };
          state.beaconsById = { ...state.beaconsById, ...normalized.entities.beacon };
          state.beaconsIDs = [...new Set([...state.beaconsIDs, ...normalized.result])];
          state.isDataFetch = true;
        },
      },
    ),
    fetchBeacon: create.asyncThunk(
      async (uuid: BeaconUUID, { dispatch }) => {
        const beacon = (await alerting(() => getBeacon(uuid), { dispatch })) as Beacon;
        return beacon;
      },
      {
        fulfilled: (state, { payload }) => {
          const normalized = normalize([payload], [beaconSchema]) as {
            entities: { beacon: Record<BeaconUUID, NormalizedBeacon> };
            result: BeaconUUID[];
          };
          state.beaconsById = { ...state.beaconsById, ...normalized.entities.beacon };
          state.beaconsIDs = [...new Set([...state.beaconsIDs, ...normalized.result])];
          state.isDataFetch = true;
        },
      },
    ),
    fetchBeaconEquipmentHistory: create.asyncThunk(
      async (uuid: BeaconUUID, { dispatch }) => {
        const history = (await alerting(() => getBeaconEquipmentHistory(uuid), {
          dispatch,
        })) as BeaconEquipmentHistory[];
        return history;
      },
      {
        fulfilled: (state, { payload, meta: { arg: uuid } }) => {
          state.beaconEquipmentHistoryByID[uuid] = payload;
        },
      },
    ),
    fetchBeaconDriverHistory: create.asyncThunk(
      async (uuid: BeaconUUID, { dispatch }) => {
        const history = (await alerting(() => getBeaconDriverHistory(uuid), {
          dispatch,
        })) as BeaconDriverHistory[];
        return history;
      },
      {
        fulfilled: (state, { payload, meta: { arg: uuid } }) => {
          state.beaconDriverHistoryByID[uuid] = payload;
        },
      },
    ),
    linkBeaconToEquipment: create.asyncThunk(
      async (
        {
          beaconUUID,
          payload,
        }: {
          beaconUUID: BeaconUUID;
          payload: { equipment_id: EquipmentId; from_date: string; to_date?: string };
        },
        { dispatch },
      ) => {
        const newHistory = (await alerting(() => linkBeaconToEquipmentApi(beaconUUID, payload), {
          dispatch,
        })) as BeaconEquipmentHistory[];
        success('Beacon.update_success', { dispatch });
        return { beaconID: beaconUUID, equipmentID: payload.equipment_id, history: newHistory };
      },
      {
        fulfilled: (state, { payload: { history: newHistory, beaconID: beaconUUID, equipmentID } }) => {
          const beacon = state.beaconsById[beaconUUID];
          if (beacon) {
            beacon.currentEquipmentInstanceId = equipmentID;
          }
          state.beaconEquipmentHistoryByID[beaconUUID] = newHistory;
        },
      },
    ),
    linkBeaconToDriver: create.asyncThunk(
      async (
        {
          beaconUUID,
          payload,
        }: { beaconUUID: BeaconUUID; payload: { user_id: UserId; from_date: string; to_date?: string } },
        { dispatch },
      ) => {
        const newHistory = (await alerting(() => linkBeaconToDriverApi(beaconUUID, payload), {
          dispatch,
        })) as BeaconDriverHistory[];
        success('Beacon.update_success', { dispatch });
        return newHistory;
      },
      {
        fulfilled: (
          state,
          {
            payload: newHistory,
            meta: {
              arg: { beaconUUID, payload },
            },
          },
        ) => {
          const beacon = state.beaconsById[beaconUUID];
          if (beacon) {
            beacon.currentUserId = payload.user_id;
          }
          state.beaconDriverHistoryByID[beaconUUID] = newHistory;
        },
      },
    ),
    unlinkDriverBeacon: create.asyncThunk(
      async (beaconUUID: BeaconUUID, { dispatch }) => {
        const newHistory = (await alerting(() => unlinkDriverBeaconApi(beaconUUID), {
          dispatch,
        })) as BeaconDriverHistory[];
        success('Beacon.delete_success', { dispatch });
        return newHistory;
      },
      {
        fulfilled: (state, { payload, meta: { arg: beaconUUID } }) => {
          const beacon = state.beaconsById[beaconUUID];
          if (beacon) {
            beacon.currentUserId = undefined;
            beacon.currentEquipmentInstanceId = undefined;
          }
          state.beaconDriverHistoryByID[beaconUUID] = payload;
        },
      },
    ),
    updateDriverBeaconEntry: create.asyncThunk(async (entry: BeaconDriverHistory, { dispatch }) => {
      await alerting(() => updateDriverBeaconEntryApi(entry.id, entry), { dispatch });
      await dispatch(fetchBeaconDriverHistory(entry.beacon_uuid));
      success('Beacon.update_success', { dispatch });
    }),
    deleteDriverBeaconEntry: create.asyncThunk(async (entry: BeaconDriverHistory, { dispatch }) => {
      await alerting(() => deleteDriverBeaconEntryApi(entry.id), { dispatch });
      await dispatch(fetchBeaconDriverHistory(entry.beacon_uuid));
      success('Beacon.delete_success', { dispatch });
    }),
    removedDriverFromBeacon: create.reducer(
      (state, { payload: beaconHistoryId }: { payload: BeaconDriverHistoryId }) => {
        const beacon = state.beaconsById[beaconHistoryId];
        if (beacon) {
          beacon.currentUserId = undefined;
        }
      },
    ),
    linkTagToBeacon: create.asyncThunk(
      async (
        {
          beaconUUID,
          tag,
        }: {
          beaconUUID: BeaconUUID;
          tag: Tag;
        },
        { dispatch },
      ) => {
        await alerting(() => linkTagToBeaconApi(beaconUUID, tag.id), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { beaconUUID, tag },
            },
          },
        ) => {
          const beacon = state.beaconsById[beaconUUID];
          if (beacon) {
            if (!beacon.tags) {
              beacon.tags = [];
            }
            beacon.tags.push(tag);
          }
        },
      },
    ),
    unlinkTagFromBeacon: create.asyncThunk(
      async (
        {
          beaconUUID,
          tagId,
        }: {
          beaconUUID: BeaconUUID;
          tagId: TagId;
        },
        { dispatch },
      ) => {
        await alerting(() => unlinkTagFromBeaconApi(beaconUUID, tagId), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { beaconUUID, tagId },
            },
          },
        ) => {
          const beacon = state.beaconsById[beaconUUID];
          if (beacon && beacon.tags) {
            beacon.tags = beacon.tags.filter((tag) => tag.id !== tagId);
          }
        },
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addCase(driverAndBeaconLinked, (state, { payload: { id, driverID, history } }) => {
      const beacon = state.beaconsById[id];
      if (beacon) {
        beacon.currentUserId = driverID;
      }
      state.beaconDriverHistoryByID[id] = history;
    });
  },
  selectors: {
    selectBeaconsIds: (state) => state.beaconsIDs,
    selectBeaconsById: (state) => state.beaconsById,
    selectBeaconEquipmentHistoryById: (state) => state.beaconEquipmentHistoryByID,
    selectBeaconDriverHistoryById: (state) => state.beaconDriverHistoryByID,
    selectIsBeaconDataFetched: (state) => state.isDataFetch,
  },
});

export const { selectBeaconDriverHistoryById, selectBeaconEquipmentHistoryById, selectBeaconsById, selectBeaconsIds } =
  beaconsSlice.selectors;

export const {
  fetchBeacons,
  fetchBeacon,
  fetchBeaconEquipmentHistory,
  fetchBeaconDriverHistory,
  linkBeaconToEquipment,
  linkBeaconToDriver,
  unlinkDriverBeacon,
  updateDriverBeaconEntry,
  deleteDriverBeaconEntry,
  removedDriverFromBeacon,
  linkTagToBeacon,
  unlinkTagFromBeacon,
} = beaconsSlice.actions;

export default beaconsSlice.reducer;
